/**
 * Builds stable Persian/RTL field labels for runtime and designer previews.
 *
 * A blank title produces no visual label, even when the field is required.
 * Long labels prefer a semantic second line (parenthetical guidance or a
 * second sentence) instead of allowing the browser to leave a few words from
 * that sentence at the end of the first line.
 */
import { type ReactNode } from 'react';
import { readRuntimeUiLocale } from '../app/ui-locale';
import { publicCopy } from '../features/renderer/public-copy';
import type { FormField } from '../models/form';

export interface FieldLabelOptions {
  readOnly?: boolean;
}

export function normalizedFieldTitle(field: Pick<FormField, 'title'>): string {
  return String(field.title ?? '').replace(/\s+/gu, ' ').trim();
}

export function hasVisibleFieldTitle(field: Pick<FormField, 'title'>): boolean {
  return normalizedFieldTitle(field).length > 0;
}

function splitSemanticLines(title: string): { primary: string; secondary?: string } {
  // Keep the entire trailing parenthetical instruction on its own line.
  const parenthetical = /^(.*?)(\s*[\(（][\s\S]+[\)）]\s*)$/u.exec(title);
  if (parenthetical?.[1]?.trim() && parenthetical[2]?.trim()) {
    return { primary: parenthetical[1].trim(), secondary: parenthetical[2].trim() };
  }

  // Prefer a true sentence boundary when a second sentence exists.
  const sentence = /^(.+?[.!؟؛])\s+(.+)$/u.exec(title);
  if (sentence?.[1]?.trim() && sentence[2]?.trim()) {
    return { primary: sentence[1].trim(), secondary: sentence[2].trim() };
  }

  // Common Persian guidance words are also useful semantic break points.
  const guidance = /^(.+?)\s+((?:لطفاً|لطفا|توجه|راهنما)\b[\s\S]+)$/u.exec(title);
  if (guidance?.[1]?.trim() && guidance[2]?.trim()) {
    return { primary: guidance[1].trim(), secondary: guidance[2].trim() };
  }

  return { primary: title };
}

function requiredText(text: string, required: boolean): ReactNode {
  if (!required) return text;
  const match = /^(.*?)(\S+)\s*$/u.exec(text);
  const prefix = match?.[1] ?? '';
  const tail = match?.[2] ?? text;
  return <>
    {prefix}
    <span className="df-label-required-tail">{tail}<span className="df-required-mark" aria-hidden="true"> *</span></span>
    <span className="sr-only"> ({publicCopy(readRuntimeUiLocale()).required})</span>
  </>;
}

export function renderFieldLabel(field: Pick<FormField, 'title' | 'validation'>, options: FieldLabelOptions = {}): ReactNode {
  const title = normalizedFieldTitle(field);
  if (!title) return null;

  const lines = splitSemanticLines(title);
  const required = !options.readOnly && Boolean(field.validation?.required);
  return <>
    <span className="df-label-primary-line">
      {requiredText(lines.primary, required && !lines.secondary)}
    </span>
    {lines.secondary ? <span className="df-label-secondary-line">
      {requiredText(lines.secondary, required)}
    </span> : null}
  </>;
}
